import { useCallback, useEffect, useState } from 'react';

import type {
  ChoiceOption,
  CutsheetQuestionnaire,
  DefaultSelections,
  PricingOverride,
  Question,
} from '../types';

import {
  CutsheetQuestionnaireDefaultSelectionsInput,
  EnumCutsheetQuestionnaireDefaultSelectionsSelectedCutsSpecificationsType,
  EnumCutsheetQuestionnaireDefaultSelectionsTrimSelectionsType,
  EnumCutsheetQuestionnaireQuestionsType,
  useCutsheetQuestionnaireByIdQuery,
  useCutsheetQuestionnaireDeleteMutation,
  useCutsheetQuestionnaireManyQuery,
  useCutsheetQuestionnaireUpsertMutation,
} from 'lib/graphql';

/**
 * Drops Apollo cache metadata (`__typename`) from an object so it can be
 * passed as a GraphQL mutation input without the server rejecting unknown
 * fields. Returns `undefined` for null/undefined inputs.
 * @param obj - Object that may contain `__typename`
 */
function stripTypename<T extends object>(
  obj: T | null | undefined,
): Omit<T, '__typename'> | undefined {
  if (!obj) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename, ...rest } = obj as T & { __typename?: unknown };
  return rest as Omit<T, '__typename'>;
}

/**
 * Converts the local DefaultSelections shape to the GQL upsert input type.
 *
 * Defined at module level so it stays at shallow indentation, keeping all
 * lines within Prettier's 80-character limit.
 * @param sel - Local default-selections object or undefined
 * @returns GQL-compatible input, or undefined when there are no selections
 */
function buildDefaultSelectionsInput(
  sel: DefaultSelections | undefined,
): CutsheetQuestionnaireDefaultSelectionsInput | undefined {
  if (!sel) return undefined;
  // Local aliases keep the cast expressions within the 80-char line limit.
  type SpecType =
    EnumCutsheetQuestionnaireDefaultSelectionsSelectedCutsSpecificationsType;
  type TrimType = EnumCutsheetQuestionnaireDefaultSelectionsTrimSelectionsType;
  return {
    selectedCuts: sel.selectedCuts.map((c) => ({
      optionId: c.optionId,
      specifications: c.specifications?.map((s) => ({
        specification: s.specification,
        type: s.type as SpecType,
        value: s.value,
      })),
    })),
    trimSelections: sel.trimSelections
      ? {
          type: sel.trimSelections.type as TrimType,
          trim: sel.trimSelections.trim.map((t) => ({
            optionId: t.optionId,
            ...('rank' in t
              ? { rank: t.rank, quantity: t.quantity }
              : { percentage: t.percentage }),
            specifications: t.specifications?.map((s) => ({
              specification: s.specification,
              type: s.type as SpecType,
              value: s.value,
            })),
          })),
        }
      : undefined,
    specifications: sel.specifications?.map((s) => ({
      specification: s.specification,
      type: s.type as SpecType,
      value: s.value,
    })),
    primalNotes: sel.primalNotes
      ? Object.entries(sel.primalNotes).map(([id, note]) => ({ id, note }))
      : undefined,
    trimNotes: sel.trimNotes
      ? Object.entries(sel.trimNotes).map(([id, note]) => ({ id, note }))
      : undefined,
  } as any;
}

export interface UseQuestionnaireStateOptions {
  questionnaireId?: string;
  capabilityId?: string;
  /** Whether to fetch only active questionnaires */
  isActive?: boolean;
  /**
   * Skip querying by questionnaire ID and resolve from capability list only.
   * Useful in contexts where by-id resolvers are scoped to the current user.
   */
  skipByIdQuery?: boolean;
}

/**
 * Hook for managing cutsheet questionnaire state with GraphQL
 *
 * Can either:
 * - Fetch a specific questionnaire by ID
 * - Fetch all questionnaires for a vendor and animal type
 *
 * Provides CRUD operations for questionnaires and questions
 * @param options - Configuration for fetching questionnaires
 * @param options.questionnaireId
 * @param options.capabilityId
 * @param options.isActive
 * @returns Questionnaire data and operations
 */
export const useQuestionnaireState = ({
  questionnaireId,
  capabilityId,
  isActive = true,
  skipByIdQuery = false,
}: UseQuestionnaireStateOptions) => {
  // Fetch specific questionnaire by ID
  const {
    data: questionnaireData,
    loading: questionnaireLoading,
    error: questionnaireError,
    refetch: refetchQuestionnaire,
  } = useCutsheetQuestionnaireByIdQuery({
    variables: { id: questionnaireId },
    skip: !questionnaireId || skipByIdQuery,
  });

  // Fetch all questionnaires for vendor/animal
  const {
    data: questionnairesData,
    loading: questionnairesLoading,
    error: questionnairesError,
    refetch: refetchQuestionnaires,
  } = useCutsheetQuestionnaireManyQuery({
    variables: {
      capabilityId,
      status: isActive ? 'published' : undefined,
    },
    skip: !capabilityId,
  });

  // Mutations
  const [upsertQuestionnaireMutation] =
    useCutsheetQuestionnaireUpsertMutation();
  const [deleteQuestionnaireMutation] =
    useCutsheetQuestionnaireDeleteMutation();

  // Transform GraphQL data to match frontend types
  const transformQuestion = (item: any): Question => {
    const resolvedId = item._id ?? item.id ?? crypto.randomUUID();
    const base = {
      // Questions are sub-entities — MongoDB may not expose _id on them yet.
      // Fall back to a UUID so every question has a stable client-side id for
      // dnd-kit dropzone registration, state lookups, etc.
      id: resolvedId,
      title: item.title,
      description: item.description,
      required: item.required,
      sortOrder: item.sortOrder,
    };

    switch (item.type) {
      case EnumCutsheetQuestionnaireQuestionsType.InfoSlide:
        return {
          ...base,
          type: EnumCutsheetQuestionnaireQuestionsType.InfoSlide,
          details: item.details,
          imageUrl: item.imageUrl,
        };
      case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec:
        return {
          ...base,
          type: EnumCutsheetQuestionnaireQuestionsType.GlobalSpec,
          specificationId: item.specificationId,
        };
      case EnumCutsheetQuestionnaireQuestionsType.FreeSelection:
        return {
          ...base,
          type: EnumCutsheetQuestionnaireQuestionsType.FreeSelection,
          availableCutIds: item.availableCutIds,
          allowMultiple: item.allowMultiple,
          minSelections: item.minSelections,
          maxSelections: item.maxSelections,
        };
      case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup:
        return {
          ...base,
          type: EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup,
          allowSubSelection: item.allowSubSelection,
          options:
            item.options?.map((opt: any) => ({
              // The GQL fragment returns `id` (not `_id`) for nested option objects
              id: opt.id ?? opt._id,
              label: opt.label,
              description: opt.description,
              imageUrl: opt.imageUrl,
              cutIds: opt.cutIds,
            })) || [],
        };
      case EnumCutsheetQuestionnaireQuestionsType.TrimRanking:
        return {
          ...base,
          type: EnumCutsheetQuestionnaireQuestionsType.TrimRanking,
          availableTrimIds: item.availableTrimIds,
          maxSelections: item.maxSelections,
        };
      case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage:
        return {
          ...base,
          type: EnumCutsheetQuestionnaireQuestionsType.TrimPercentage,
          availableTrimIds: item.availableTrimIds,
          requireExactTotal: item.requireExactTotal,
        };
      case EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection:
        return {
          ...base,
          type: EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection,
          availableExtraIds: item.availableExtraIds,
          allowMultiple: item.allowMultiple,
          minSelections: item.minSelections,
          maxSelections: item.maxSelections,
        };
      default:
        return base as Question;
    }
  };

  const transformQuestionnaire = (item: any): CutsheetQuestionnaire => {
    // pricingOverrides comes back from GraphQL as a plain JSON object.
    const pricingOverrides: Record<string, PricingOverride> | undefined =
      item.pricingOverrides
        ? (item.pricingOverrides as Record<string, PricingOverride>)
        : undefined;

    // specOptionPricingOverrides: nested plain JSON — cast directly.
    const specOptionPricingOverrides:
      | Record<string, Record<string, PricingOverride>>
      | undefined = item.specOptionPricingOverrides ?? undefined;

    // defaultSelections: pre-applied cuts/trim/specs stored as an object.
    const defaultSelections: DefaultSelections | undefined =
      item.defaultSelections
        ? {
            selectedCuts: (item.defaultSelections.selectedCuts ?? []).map(
              (c: any) => ({
                optionId: c.optionId,
                specifications: c.specifications?.length
                  ? c.specifications.map((s: any) => ({
                      specification: s.specification,
                      type: s.type,
                      value: s.value,
                    }))
                  : undefined,
              }),
            ),
            trimSelections: item.defaultSelections.trimSelections
              ? {
                  type: item.defaultSelections.trimSelections.type,
                  trim: (item.defaultSelections.trimSelections.trim ?? []).map(
                    (t: any) => ({
                      optionId: t.optionId,
                      ...(t.rank !== undefined
                        ? { rank: t.rank, quantity: t.quantity ?? 0 }
                        : { percentage: t.percentage ?? 0 }),
                      specifications: t.specifications?.length
                        ? t.specifications.map((s: any) => ({
                            specification: s.specification,
                            type: s.type,
                            value: s.value,
                          }))
                        : undefined,
                    }),
                  ),
                }
              : undefined,
            specifications: item.defaultSelections.specifications?.length
              ? item.defaultSelections.specifications.map((s: any) => ({
                  specification: s.specification,
                  type: s.type,
                  value: s.value,
                }))
              : undefined,
            primalNotes: item.defaultSelections.primalNotes?.length
              ? Object.fromEntries(
                  item.defaultSelections.primalNotes.map(
                    ({ id, note }: { id: string; note: string }) => [id, note],
                  ),
                )
              : undefined,
            trimNotes: item.defaultSelections.trimNotes?.length
              ? Object.fromEntries(
                  item.defaultSelections.trimNotes.map(
                    ({ id, note }: { id: string; note: string }) => [id, note],
                  ),
                )
              : undefined,
          }
        : undefined;

    return {
      id: item._id,
      capabilityId: item.capabilityId || '',
      vendorId: item.vendor,
      name: item.name,
      description: item.description,
      questions: item.questions?.map(transformQuestion) || [],
      pricingOverrides,
      specOptionPricingOverrides,
      defaultSelections,
      useCustomPrintSettings: item.useCustomPrintSettings ?? false,
      printSettings: stripTypename(item.printSettings),
      status: item.status ?? 'draft',
      createdAt: new Date(item.createdAt),
      updatedAt: new Date(item.updatedAt),
    };
  };

  // The persisted questionnaire (derived from Apollo cache)
  const matchedQuestionnaireFromList = questionnaireId
    ? questionnairesData?.cutsheetQuestionnairesByVendorAndAnimal?.find(
        (q) => q?._id === questionnaireId,
      )
    : null;

  const savedQuestionnaire = questionnaireData?.cutsheetQuestionnaireById
    ? transformQuestionnaire(questionnaireData.cutsheetQuestionnaireById)
    : matchedQuestionnaireFromList
      ? transformQuestionnaire(matchedQuestionnaireFromList)
      : questionnairesData?.cutsheetQuestionnairesByVendorAndAnimal?.[0]
        ? transformQuestionnaire(
            questionnairesData.cutsheetQuestionnairesByVendorAndAnimal[0],
          )
        : null;

  const questionnaires =
    questionnairesData?.cutsheetQuestionnairesByVendorAndAnimal?.map(
      transformQuestionnaire,
    ) || [];

  // Local draft state — all edits are staged here until the user explicitly saves
  const [draftQuestionnaire, setDraftQuestionnaire] =
    useState<CutsheetQuestionnaire | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Sync draft from Apollo on initial load or after a successful save (isDirty resets to false).
  // Depends on the raw Apollo data objects (stable references) rather than the derived
  // `savedQuestionnaire` (which is a new object every render due to transformQuestionnaire),
  // to avoid an infinite setState → re-render → setState loop.
  // eslint-disable-next-line
  useEffect(() => {
    if (!isDirty) {
      setDraftQuestionnaire(savedQuestionnaire);
    }
  }, [questionnaireData, questionnairesData]);

  // Persist the current draft to the backend (optionally override fields like isPublished)
  const saveQuestionnaire = useCallback(
    async (overrides?: Partial<CutsheetQuestionnaire>) => {
      // Merge optional caller overrides (e.g. { isPublished: true }) with the local draft
      const data = overrides
        ? { ...draftQuestionnaire!, ...overrides }
        : draftQuestionnaire;

      if (!data?.capabilityId || !data?.name) {
        throw new Error('capabilityId and name are required');
      }

      // Transform questions for GraphQL input (support all question types)
      // Note: `id` is a frontend-only field used for list management (DnD, etc.)
      // and is NOT sent to the backend — the backend assigns its own _id.
      const questionInputs = data.questions?.map((q) => {
        const baseInput = {
          type: q.type,
          title: q.title,
          description: q.description,
          required: q.required || false,
          sortOrder: q.sortOrder,
        };

        switch (q.type) {
          case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec:
            return {
              ...baseInput,
              specificationId: q.specificationId,
            };
          case EnumCutsheetQuestionnaireQuestionsType.FreeSelection:
            return {
              ...baseInput,
              availableCutIds: q.availableCutIds,
              allowMultiple: q.allowMultiple,
              // Only send minSelections when the question is required
              minSelections: q.required ? q.minSelections : undefined,
              maxSelections: q.maxSelections,
            };
          case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup:
            return {
              ...baseInput,
              allowSubSelection: q.allowSubSelection,
              // Strip frontend-only `id` — CutsheetChoiceOptionInput only accepts label/description/imageUrl/cutIds
              options: q.options.map(
                ({ label, description, imageUrl, cutIds }) => ({
                  label,
                  description,
                  imageUrl,
                  cutIds,
                }),
              ),
            };
          case EnumCutsheetQuestionnaireQuestionsType.TrimRanking:
            return {
              ...baseInput,
              availableTrimIds: q.availableTrimIds,
              maxSelections: q.maxSelections,
            };
          case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage:
            return {
              ...baseInput,
              availableTrimIds: q.availableTrimIds,
              requireExactTotal: q.requireExactTotal,
            };
          case EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection:
            return {
              ...baseInput,
              availableExtraIds: q.availableExtraIds,
              allowMultiple: q.allowMultiple,
              minSelections: q.required ? q.minSelections : undefined,
              maxSelections: q.maxSelections,
            };
          case EnumCutsheetQuestionnaireQuestionsType.InfoSlide:
            return {
              ...baseInput,
              imageUrl: q.imageUrl,
              details: q.details,
            };
          default:
            return baseInput;
        }
      });

      setIsSaving(true);
      try {
        const result = await upsertQuestionnaireMutation({
          variables: {
            id: data.id || questionnaireId,
            capabilityId: data.capabilityId,
            name: data.name,
            description: data.description,
            questions: questionInputs,
            // Convert pricingOverrides record to the array-of-entries input shape
            pricingOverrides: data.pricingOverrides
              ? Object.entries(data.pricingOverrides).map(([id, pricing]) => ({
                  id,
                  pricing,
                }))
              : undefined,
            specOptionPricingOverrides: data.specOptionPricingOverrides
              ? Object.entries(data.specOptionPricingOverrides).map(
                  ([specId, optionMap]) => ({
                    specId,
                    options: Object.entries(optionMap).map(
                      ([optionValue, pricing]) => ({ optionValue, pricing }),
                    ),
                  }),
                )
              : undefined,
            // Send defaultSelections as a structured object matching the new schema
            defaultSelections: buildDefaultSelectionsInput(
              data.defaultSelections,
            ),
            useCustomPrintSettings: data.useCustomPrintSettings ?? false,
            // Strip Apollo's `__typename` before sending — GraphQL input types
            // reject unknown fields.
            printSettings: stripTypename(data.printSettings),
            status: data.status,
          },
        });

        // Mark clean and refresh Apollo cache so savedQuestionnaire syncs the draft
        setIsDirty(false);
        if (questionnaireId) {
          refetchQuestionnaire();
        } else if (capabilityId) {
          refetchQuestionnaires();
        }

        return result.data?.cutsheetQuestionnaireUpsert;
      } finally {
        setIsSaving(false);
      }
    },
    [
      draftQuestionnaire,
      questionnaireId,
      capabilityId,
      upsertQuestionnaireMutation,
      refetchQuestionnaire,
      refetchQuestionnaires,
    ],
  );

  // Question operations — all update local draft state only (no network call)
  const addQuestion = useCallback(
    (question: Omit<Question, 'id' | 'sortOrder'>) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const newQuestion = {
          ...question,
          id: crypto.randomUUID(),
          sortOrder: prev.questions.length,
        } as Question;
        return { ...prev, questions: [...prev.questions, newQuestion] };
      });
      setIsDirty(true);
    },
    [],
  );

  /**
   * Add a question at a specific position in the questionnaire.
   * Questions after the insertion point are re-indexed.
   */
  const addQuestionAtIndex = useCallback(
    (question: Omit<Question, 'id' | 'sortOrder'>, insertAtIndex: number) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const sorted = [...prev.questions].sort(
          (a, b) => a.sortOrder - b.sortOrder,
        );
        const newQuestion = {
          ...question,
          id: crypto.randomUUID(),
          sortOrder: insertAtIndex,
        } as Question;
        sorted.splice(insertAtIndex, 0, newQuestion);
        const reindexed = sorted.map((q, i) => ({ ...q, sortOrder: i }));
        return { ...prev, questions: reindexed };
      });
      setIsDirty(true);
    },
    [],
  );

  const updateQuestion = useCallback(
    (id: string, updates: Partial<Question>) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const updatedQuestions = prev.questions.map((q) =>
          q.id === id ? ({ ...q, ...updates } as Question) : q,
        );
        return { ...prev, questions: updatedQuestions };
      });
      setIsDirty(true);
    },
    [],
  );

  /**
   * Atomically adds one or more item IDs to a question's available items list.
   * All logic runs inside the functional setState callback so it always
   * operates on the latest draft state — never a stale closure snapshot.
   *
   * - FreeSelection: appends to availableCutIds (deduplicates)
   * - TrimRanking / TrimPercentage: appends to availableTrimIds (deduplicates)
   * - GlobalSpec: replaces specificationId with the last supplied ID
   */
  const addItemsToQuestion = useCallback(
    (questionId: string, itemIds: string[]) => {
      if (itemIds.length === 0) return;
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const updatedQuestions = prev.questions.map((q) => {
          if (q.id !== questionId) return q;
          if (q.type === EnumCutsheetQuestionnaireQuestionsType.FreeSelection) {
            const current = q.availableCutIds ?? [];
            return {
              ...q,
              availableCutIds: [
                ...current,
                ...itemIds.filter((id) => !current.includes(id)),
              ],
            } as Question;
          }
          if (
            q.type === EnumCutsheetQuestionnaireQuestionsType.TrimRanking ||
            q.type === EnumCutsheetQuestionnaireQuestionsType.TrimPercentage
          ) {
            const current = q.availableTrimIds ?? [];
            return {
              ...q,
              availableTrimIds: [
                ...current,
                ...itemIds.filter((id) => !current.includes(id)),
              ],
            } as Question;
          }
          if (
            q.type === EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection
          ) {
            const current = q.availableExtraIds ?? [];
            return {
              ...q,
              availableExtraIds: [
                ...current,
                ...itemIds.filter((id) => !current.includes(id)),
              ],
            } as Question;
          }
          if (q.type === EnumCutsheetQuestionnaireQuestionsType.GlobalSpec) {
            // Single-select: replace with the last supplied id
            return {
              ...q,
              specificationId: itemIds[itemIds.length - 1],
            } as Question;
          }
          return q;
        });
        return { ...prev, questions: updatedQuestions };
      });
      setIsDirty(true);
    },
    [],
  );

  /**
   * Atomically removes an item from a question's available items list.
   * Runs inside functional setState to avoid stale-closure issues.
   */
  const removeItemFromQuestion = useCallback(
    (questionId: string, itemId: string) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const updatedQuestions = prev.questions.map((q) => {
          if (q.id !== questionId) return q;
          if (q.type === EnumCutsheetQuestionnaireQuestionsType.FreeSelection) {
            return {
              ...q,
              availableCutIds: q.availableCutIds.filter((id) => id !== itemId),
            } as Question;
          }
          if (
            q.type === EnumCutsheetQuestionnaireQuestionsType.TrimRanking ||
            q.type === EnumCutsheetQuestionnaireQuestionsType.TrimPercentage
          ) {
            return {
              ...q,
              availableTrimIds: q.availableTrimIds.filter(
                (id) => id !== itemId,
              ),
            } as Question;
          }
          if (
            q.type === EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection
          ) {
            return {
              ...q,
              availableExtraIds: q.availableExtraIds.filter(
                (id) => id !== itemId,
              ),
            } as Question;
          }
          if (q.type === EnumCutsheetQuestionnaireQuestionsType.GlobalSpec) {
            return { ...q, specificationId: '' } as Question;
          }
          return q;
        });
        return { ...prev, questions: updatedQuestions };
      });
      setIsDirty(true);
    },
    [],
  );

  /**
   * Reorders items within a single question (e.g. FreeSelection cuts or trim
   * ranking trims). `newOrderIds` is the complete id list in the desired order.
   */
  const reorderItemsInQuestion = useCallback(
    (questionId: string, newOrderIds: string[]) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const updatedQuestions = prev.questions.map((q) => {
          if (q.id !== questionId) return q;
          if (q.type === EnumCutsheetQuestionnaireQuestionsType.FreeSelection) {
            return { ...q, availableCutIds: newOrderIds } as Question;
          }
          if (
            q.type === EnumCutsheetQuestionnaireQuestionsType.TrimRanking ||
            q.type === EnumCutsheetQuestionnaireQuestionsType.TrimPercentage
          ) {
            return { ...q, availableTrimIds: newOrderIds } as Question;
          }
          if (
            q.type === EnumCutsheetQuestionnaireQuestionsType.ExtrasSelection
          ) {
            return { ...q, availableExtraIds: newOrderIds } as Question;
          }
          return q;
        });
        return { ...prev, questions: updatedQuestions };
      });
      setIsDirty(true);
    },
    [],
  );

  const removeQuestion = useCallback((id: string) => {
    setDraftQuestionnaire((prev) => {
      if (!prev) return prev;
      return { ...prev, questions: prev.questions.filter((q) => q.id !== id) };
    });
    setIsDirty(true);
  }, []);

  const reorderQuestions = useCallback((questions: Question[]) => {
    setDraftQuestionnaire((prev) => {
      if (!prev) return prev;
      const reorderedQuestions = questions.map(
        (question, index) => ({ ...question, sortOrder: index }) as Question,
      );
      return { ...prev, questions: reorderedQuestions };
    });
    setIsDirty(true);
  }, []);

  const reorderQuestionsByIndex = useCallback(
    (oldIndex: number, newIndex: number) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const newQuestions = [...prev.questions];
        const [reorderedItem] = newQuestions.splice(oldIndex, 1);
        newQuestions.splice(newIndex, 0, reorderedItem);
        const reorderedQuestions = newQuestions.map(
          (question, index) => ({ ...question, sortOrder: index }) as Question,
        );
        return { ...prev, questions: reorderedQuestions };
      });
      setIsDirty(true);
    },
    [],
  );

  // Choice option operations — all update local draft state only (no network call)
  const addChoiceOption = useCallback(
    (questionId: string, option: Omit<ChoiceOption, 'id'>) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const updatedQuestions = prev.questions.map((q) =>
          q.id === questionId &&
          q.type === EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup
            ? ({
                ...q,
                options: [...q.options, { ...option, id: crypto.randomUUID() }],
              } as Question)
            : q,
        );
        return { ...prev, questions: updatedQuestions };
      });
      setIsDirty(true);
    },
    [],
  );

  const updateChoiceOption = useCallback(
    (questionId: string, optionId: string, updates: Partial<ChoiceOption>) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const updatedQuestions = prev.questions.map((q) =>
          q.id === questionId &&
          q.type === EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup
            ? ({
                ...q,
                options: q.options.map((opt) =>
                  opt.id === optionId ? { ...opt, ...updates } : opt,
                ),
              } as Question)
            : q,
        );
        return { ...prev, questions: updatedQuestions };
      });
      setIsDirty(true);
    },
    [],
  );

  const removeChoiceOption = useCallback(
    (questionId: string, optionId: string) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        const updatedQuestions = prev.questions.map((q) =>
          q.id === questionId &&
          q.type === EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup
            ? ({
                ...q,
                options: q.options.filter((opt) => opt.id !== optionId),
              } as Question)
            : q,
        );
        return { ...prev, questions: updatedQuestions };
      });
      setIsDirty(true);
    },
    [],
  );

  // Questionnaire metadata operations — update local draft state only
  const updateQuestionnaireInfo = useCallback(
    (
      updates: Partial<
        Pick<
          CutsheetQuestionnaire,
          | 'name'
          | 'description'
          | 'status'
          | 'pricingOverrides'
          | 'specOptionPricingOverrides'
          | 'defaultSelections'
        >
      >,
    ) => {
      setDraftQuestionnaire((prev) => {
        if (!prev) return prev;
        return { ...prev, ...updates };
      });
      setIsDirty(true);
    },
    [],
  );

  // Toggle published state locally — caller must save via saveQuestionnaire() to persist
  const publishQuestionnaire = useCallback((publish: boolean = true) => {
    setDraftQuestionnaire((prev) => {
      if (!prev) return prev;
      return { ...prev, status: publish ? 'published' : 'draft' };
    });
    setIsDirty(true);
  }, []);

  // Delete questionnaire
  const deleteQuestionnaire = useCallback(
    async (id?: string) => {
      const idToDelete = id || questionnaireId;
      if (!idToDelete) return;

      await deleteQuestionnaireMutation({ variables: { id: idToDelete } });

      if (capabilityId) {
        refetchQuestionnaires();
      }
    },
    [
      questionnaireId,
      capabilityId,
      deleteQuestionnaireMutation,
      refetchQuestionnaires,
    ],
  );

  const loading = questionnaireLoading || questionnairesLoading;
  const error = questionnaireError || questionnairesError;

  return {
    // The local draft — all edits land here before being persisted
    questionnaire: draftQuestionnaire,
    questionnaires,
    loading,
    error,
    isDirty,
    isSaving,
    // Save operations
    saveQuestionnaire,
    deleteQuestionnaire,
    // Question operations
    addQuestion,
    addQuestionAtIndex,
    updateQuestion,
    removeQuestion,
    addItemsToQuestion,
    removeItemFromQuestion,
    reorderItemsInQuestion,
    reorderQuestions,
    reorderQuestionsByIndex,
    // Choice option operations
    addChoiceOption,
    updateChoiceOption,
    removeChoiceOption,
    // Questionnaire metadata
    updateQuestionnaireInfo,
    publishQuestionnaire,
    // Refetch
    refetch: () => {
      if (questionnaireId) {
        refetchQuestionnaire();
      } else if (capabilityId) {
        refetchQuestionnaires();
      }
    },
  };
};
